import type { FC } from 'react'
import { useState } from 'react'

import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'

import { postOrder } from '@/api/shop'
import { getDevicePolicy, getDevices, getModels, getSettings } from '@/ducks/devicePolicyReducer'
import useSetup from '@/hooks/setup'
import useUser, { useIsMultiConsult } from '@/hooks/user'

import { Translation } from '../i18n/Translation'

import Inventory from './Inventory'
import Order2ModelPrice from './Order2ModelPrice'
import Order3Addons from './Order3Addons'
import Order4Confirmation from './Order4Confirmation'
import { addOrderAddon, removeOrderAddon, setOrderAddress } from './OrderRepository'
import OrderStatus from './OrderStatus'
import type {
  Order as OrderType,
  OrderInfo,
  Product,
  Settings,
  Inventory as InventoryType,
} from './types'

import './style/shop.scss'

const Content: FC<{
  order: OrderType | null
  orderStage: number
  onOrderInfoSet: (v: Partial<OrderInfo>) => void
  onOrderSet: (order: OrderType | null) => void
  onOrderCreate: (product: Product, remainder: number, downPaymentDuration: number) => void
}> = ({ order, orderStage, onOrderSet, onOrderInfoSet, onOrderCreate }) => {
  const params = useParams()
  const dispatch = useDispatch()

  const setup = useSetup()
  const isMultiConsult = useIsMultiConsult()
  const user = useUser()

  const settings: Settings = useSelector(getSettings)
  const models: Record<string, InventoryType> = useSelector(getModels)
  const devices: string[] = useSelector(getDevices)

  const [selectedDevice, setSelectedDevice] = useState<string | null>(null)
  const [selectedModel, setSelectedModel] = useState<string | null>(null)

  const [isOrderError, setIsOrderError] = useState(false)
  const [orderFeedBack, setOrderFeedBack] = useState<string | null>(null)

  const getSelectedDeviceModels = () => {
    if (selectedDevice === null || Object.keys(models).length === 0) return null

    return models[selectedDevice]
  }

  if (!setup) {
    return (
      <div id="no-access">
        <Translation id="Din arbeidsgiver har ikke aktivert muligheten for å kjøpe mobil." />
      </div>
    )
  }

  if (params.subSection === 'accessories') {
    const email = isMultiConsult
      ? 'kundeservice@telehuset.tromso.no'
      : 'bestilling@teleconsulting.no'

    return (
      <div className="accessories-temp">
        <Translation id="Tilbehør kan bestilles når du søker om ny mobil." />
        <br />
        <br />

        <Translation
          id="Dersom tilbehøret du ønsker ikke er på lager kan du bestille dette her ved å sende en epost til: {email}"
          values={{ email }}
        />
      </div>
    )
  }

  const postCurrentOrder = (isBuyout: boolean) => {
    const payload = {
      ...order,
      isBuyout,
    }

    if (!user) return

    postOrder({ order: payload, phoneNumber: user?.code })
      .then(result => {
        if (!result.error) {
          setSelectedModel(null)
          setSelectedDevice(null)
          setOrderFeedBack(null)
          setIsOrderError(false)
          onOrderInfoSet({
            orderStep1done: false,
            orderStep2done: false,
            orderStep3done: false,
            orderStep4done: false,
          })
          // @ts-ignore
          dispatch(getDevicePolicy(user?.code))
        } else {
          setOrderFeedBack(result.message.strings.join(' '))
          setIsOrderError(result.error)
        }
      })
      .catch(() => {
        setOrderFeedBack('Ukjent feil')
        setIsOrderError(true)
      })
  }

  const cancelOrder = () => {
    onOrderInfoSet({
      orderStep1done: false,
      orderStep2done: false,
      orderStep3done: false,
      orderStep4done: false,
    })

    setTimeout(() => {
      onOrderSet(null)
    }, 1000) // wait for animation to finish
  }

  const setDevice = (device: string) => {
    setSelectedDevice(device)
    setSelectedModel(null)
    onOrderInfoSet({ orderStep1done: true })
  }

  const setModel = (model: string) => {
    setSelectedModel(model)
    setIsOrderError(false)
    setOrderFeedBack(null)
  }

  const setAddress = (address: string, zip: string, city: string) => {
    const updatedOrder = setOrderAddress(order, address, zip, city)
    onOrderSet(updatedOrder)
  }

  const addAddon = (productId: number) => {
    const updatedOrder = addOrderAddon(order, productId)
    onOrderSet(updatedOrder)
  }

  const removeAddon = (productId: number) => {
    const updatedOrder = removeOrderAddon(order, productId)
    onOrderSet(updatedOrder)
  }

  return (
    <div>
      <Inventory
        display={Boolean(settings)}
        devices={devices}
        models={models}
        onDeviceSelect={setDevice}
      />

      <Order2ModelPrice
        display={orderStage === 2}
        settings={settings}
        selectedModel={selectedModel}
        models={getSelectedDeviceModels()}
        createOrder={onOrderCreate}
        setSelectedModel={setModel}
        cancelOrder={cancelOrder}
      />

      {order && (
        <Order3Addons
          models={models}
          order={order}
          isVisible={orderStage === 3}
          addAddon={addAddon}
          removeAddon={removeAddon}
          cancelOrder={cancelOrder}
          confirmAddons={() => onOrderInfoSet({ orderStep3done: true })}
        />
      )}

      <Order4Confirmation
        display={orderStage === 4}
        inventory={models}
        order={order}
        orderError={isOrderError}
        orderFeedBack={orderFeedBack}
        addresses={settings?.addresses}
        settings={settings}
        setOrderAddress={setAddress}
        cancelOrder={cancelOrder}
        postOrder={postCurrentOrder}
      />

      <OrderStatus activeOrder={settings?.activeOrder} asset={settings?.asset} />
    </div>
  )
}

export default Content
